import type { LiveLlmCall, ProgressNode, ProgressStep, ProgressTree } from '@localagent/sdk';
import { formatPersonaName } from '../../utils/persona-display';
import { isCognitiveTool } from './progress-tree';

export const TURN_STATUS_PHRASES = [
  'Thinking',
  'Writing the answer',
  'Drafting an answer',
  'Checking the answer',
  'Planning the steps',
  'Condensing earlier context',
  'Stopped early',
  'Looking through your files',
  'Updating files',
  'Looking something up',
  'Reading instructions',
  'Working with a specialist',
  'Working',
  'Waiting for you',
] as const;

export type TurnStatusPhrase = (typeof TURN_STATUS_PHRASES)[number];

export interface TurnStatusRow {
  personaName: string;
  phrase: TurnStatusPhrase;
  activeSince?: string;
  fanout?: { done: number; total: number };
  detail?: string;
}

type RowExtras = Pick<TurnStatusRow, 'activeSince' | 'fanout' | 'detail'>;

const FILE_READ = new Set([
  'file_read',
  'file_list',
  'file_info',
  'grep',
  'search',
  'read_file',
  'local_file_search',
  'local_grep',
  'cloud_list_versions',
  'cloud_view_unsaved',
  'result_read',
  'result_search',
]);

const FILE_WRITE = new Set([
  'file_write',
  'file_edit',
  'file_append',
  'file_delete',
  'file_move',
  'cloud_save_version',
  'cloud_restore_version',
]);

const WEB = new Set(['web_search', 'web_fetch']);

const SKILL = new Set([
  'skill_list',
  'skill_search',
  'skill_read',
  'skill_create',
  'skill_update',
  'skill_delete',
  'model_default',
  'provider_list',
  'model_list',
  'process_list',
  'docs_search',
  'docs_read',
  'workspace_init',
  'context_search',
  'context_pin',
  'context_unpin',
  'context_list',
]);

export function phraseForToolName(toolName: string | undefined): TurnStatusPhrase {
  if (!toolName) return 'Thinking';
  if (FILE_READ.has(toolName)) return 'Looking through your files';
  if (FILE_WRITE.has(toolName)) return 'Updating files';
  if (WEB.has(toolName)) return 'Looking something up';
  if (SKILL.has(toolName) || toolName.startsWith('skill_') || toolName.startsWith('context_')) {
    return 'Reading instructions';
  }
  if (isCognitiveTool(toolName)) return 'Working with a specialist';
  return 'Working';
}

type LiveCalls = ReadonlyMap<string, LiveLlmCall>;

function runningLlmPhrase(step: ProgressStep, live: LiveLlmCall | undefined): TurnStatusPhrase {
  if (live && (step.purpose ?? 'loop') === 'loop') {
    if (live.phase === 'tool_args') return phraseForToolName(live.toolName);
    if (live.phase === 'content') return step.deferred ? 'Drafting an answer' : 'Writing the answer';
  }
  switch (step.purpose) {
    case 'goal_evaluation':
      return 'Checking the answer';
    case 'process_derivation':
      return 'Planning the steps';
    case 'compaction':
      return 'Condensing earlier context';
    default:
      return step.deferred ? 'Drafting an answer' : 'Thinking';
  }
}

function stepRow(step: ProgressStep, live: LiveCalls): { phrase: TurnStatusPhrase; extras: RowExtras } | null {
  if (step.type === 'llm_call' && step.status === 'running') {
    const call = step.callId ? live.get(step.callId) : undefined;
    return {
      phrase: runningLlmPhrase(step, call),
      extras: { activeSince: step.startedAt ?? step.timestamp },
    };
  }
  if (step.type === 'tool_call') return { phrase: phraseForToolName(step.toolName), extras: {} };
  if (step.type === 'llm_call' && !step.hasToolCalls && !step.toolName) {
    return { phrase: 'Thinking', extras: {} };
  }
  if (step.type === 'triage') return { phrase: 'Working with a specialist', extras: {} };
  return null;
}

function appendRow(
  rows: TurnStatusRow[],
  personaName: string,
  phrase: TurnStatusPhrase,
  extras: RowExtras = {},
): void {
  const last = rows[rows.length - 1];
  if (last && last.personaName === personaName && last.phrase === phrase) {
    if (extras.activeSince) last.activeSince = extras.activeSince;
    if (extras.fanout) last.fanout = extras.fanout;
    if (extras.detail) last.detail = extras.detail;
    return;
  }
  rows.push({ personaName, phrase, ...extras });
}

function fanoutProgress(members: ProgressNode[]): { done: number; total: number } | null {
  if (members.length === 0) return null;
  const total = Math.max(...members.map((m) => m.fanoutSize ?? 0), members.length);
  const done = members.filter((m) => m.status !== 'running').length;
  return { done, total };
}

export function fanoutFor(node: ProgressNode, siblings: ProgressNode[]): { done: number; total: number } | null {
  const candidates = [...siblings, ...node.children].filter(
    (m) => m.fanoutId === node.id || m.fanoutId?.startsWith(`${node.id}:wave-`),
  );
  if (candidates.length === 0) return null;
  const latest = [...candidates].sort((a, b) => a.startedAt.localeCompare(b.startedAt)).at(-1)!;
  return fanoutProgress(candidates.filter((m) => m.fanoutId === latest.fanoutId));
}

function exitDetail(node: ProgressNode): string | null {
  switch (node.exitReason) {
    case 'consecutive_tool_failures': {
      const tools = [...new Set(node.failedTools ?? [])];
      const count = node.failedTools?.length ?? 0;
      const named = tools.length > 0 ? ` (${tools.join(', ')})` : '';
      return `after ${count} failed tool call${count === 1 ? '' : 's'}${named}`;
    }
    case 'max_iterations':
      return 'after reaching the step limit';
    case 'repeated_tool_calls':
      return 'after repeating the same action';
    default:
      return null;
  }
}

function walkNode(
  node: ProgressNode,
  siblings: ProgressNode[],
  fallbackPersona: string,
  rows: TurnStatusRow[],
  live: LiveCalls,
): void {
  const personaName = formatPersonaName(node.personaName || fallbackPersona);
  const specialist =
    node.cognitiveToolType != null ||
    isCognitiveTool(node.processName) ||
    (node.children.length > 0 && node.status === 'running');
  const fanout = fanoutFor(node, siblings);
  if (specialist && node.status === 'running' && node.steps.every((s) => s.type !== 'tool_call')) {
    appendRow(rows, personaName, 'Working with a specialist', fanout ? { fanout } : {});
  }
  const steps = [...node.steps].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  for (const step of steps) {
    const row = stepRow(step, live);
    if (row) appendRow(rows, personaName, row.phrase, row.extras);
  }
  const children = [...node.children].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  for (const child of children) {
    walkNode(child, children, personaName, rows, live);
  }
  const detail = node.status !== 'running' ? exitDetail(node) : null;
  if (detail) appendRow(rows, personaName, 'Stopped early', { detail });
}

export function mapTurnStatus(
  tree: ProgressTree | null | undefined,
  opts: { runPersonaName: string; interruptPending?: boolean; liveCalls?: LiveLlmCall[] },
): TurnStatusRow[] {
  const live: LiveCalls = new Map((opts.liveCalls ?? []).map((call) => [call.callId, call]));
  const runPersona = formatPersonaName(opts.runPersonaName);
  const rows: TurnStatusRow[] = [];
  if (tree) {
    const rootSteps = [...tree.steps].sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    for (const step of rootSteps) {
      const row = stepRow(step, live);
      if (row) appendRow(rows, runPersona, row.phrase, row.extras);
    }
    const roots = tree.nodes
      .filter((n) => !n.parentId)
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
    for (const node of roots) {
      walkNode(node, roots, runPersona, rows, live);
    }
    if (rows.length === 0) {
      appendRow(rows, runPersona, 'Thinking');
    }
  }
  if (opts.interruptPending) {
    const persona = rows[rows.length - 1]?.personaName ?? runPersona;
    appendRow(rows, persona, 'Waiting for you');
  }
  return rows;
}
